import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import type { Category, Item } from "@instead/shared";
import { api } from "../lib/api.ts";
import { BULK_EDIT_KEY, drafts } from "../lib/drafts.ts";
import { useT } from "../lib/i18n.ts";
import { BulkEditSheet, bulkActions, type BulkEdit, type Detour } from "../components/BulkEdit.tsx";
import { catLabel, metaLine } from "../components/DealCard.tsx";
import { Cover } from "../components/Cover.tsx";
import { CatIcon, Ic } from "../components/Icons.tsx";
import { Dialog, Toast, useToast } from "../components/Ui.tsx";
import { QuickAdd } from "../components/QuickAdd.tsx";

/**
 * Browse-first shelf (D-24): category cards, then a list inside one
 * category (sub-category chips), search across everything. Round 4:
 * progress is a word, Select starts multi-select (Edit… / Delete — D-37:
 * the Edit sheet carries every facet the item form has).
 */
type View = { kind: "browse" } | { kind: "list"; catId: string | "all" | "unsorted"; title: string; catKind: Category["kind"] | "all" | "unsorted" };
type ListView = Extract<View, { kind: "list" }>;

/** A selection in progress (D-37): where it was made, what is ticked, and the sheet's pending choices. */
interface BulkDraft { view: ListView; q: string; picked: string[]; editing: boolean; edit: BulkEdit }

export function Shelf() {
  const t = useT();
  // a selection left waiting (a detour to an editor, a restart) comes back exactly where it was
  const [initialDraft] = useState(() => drafts.read<BulkDraft>(BULK_EDIT_KEY));
  const restored = useRef<BulkDraft | null>(initialDraft);
  const [q, setQ] = useState(restored.current?.q ?? "");
  const [view, setView] = useState<View>(restored.current?.view ?? { kind: "browse" });
  const [adding, setAdding] = useState(false);
  const [toast, say] = useToast();
  const { data: catData } = useQuery({ queryKey: ["categories"], queryFn: api.categories.list });
  const cats = catData?.categories ?? [];
  const topLevel = cats.filter((c) => !c.parentId);
  const childrenOf = (id: string) => cats.filter((c) => c.parentId === id);
  const countWithChildren = (c: Category) => (c.itemCount ?? 0) + childrenOf(c.id).reduce((s, k) => s + (k.itemCount ?? 0), 0);

  if (q.trim() || view.kind === "list") {
    return (
      <ItemList
        q={q}
        setQ={setQ}
        view={view.kind === "list" ? view : { kind: "list", catId: "all", title: t("shelf.everything"), catKind: "all" }}
        onBack={() => { setQ(""); setView({ kind: "browse" }); }}
        subcats={view.kind === "list" && view.catId !== "all" && view.catId !== "unsorted" ? childrenOf(view.catId) : []}
        openCat={(c) => setView({ kind: "list", catId: c.id, title: c.name, catKind: c.kind })}
        cats={cats}
        restored={restored.current}
        consumeRestored={() => { restored.current = null; }}
      />
    );
  }

  const total = cats.reduce((s, c) => s + (c.itemCount ?? 0), 0);
  return (
    <div>
      <div className="screen-head">
        <h1 className="display">{t("shelf.title")}</h1>
        <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>{Ic.plus({ size: 14 })} {t("shelf.add")}</button>
      </div>
      {adding && <QuickAdd onClose={() => setAdding(false)} onImported={(n) => say(t("quick.imported", { n }))} />}
      <Toast msg={toast} />

      <div className="field">
        <label htmlFor="shelf-search" className="visually-hidden">{t("search")}</label>
        <input id="shelf-search" className="input" type="search" placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <nav aria-label={t("shelf.title")} className="cat-grid">
        {topLevel.map((c) => (
          <button key={c.id} type="button" className="cat-card card" onClick={() => setView({ kind: "list", catId: c.id, title: catLabel(c, t), catKind: c.kind })}>
            <span className="cat-ic" style={{ color: `var(--${c.colorToken})` }} aria-hidden="true"><CatIcon kind={c.kind} /></span>
            <span className="title">{catLabel(c, t)}</span>
            <span className="meta">{countWithChildren(c)}</span>
          </button>
        ))}
        <button type="button" className="cat-card card" onClick={() => setView({ kind: "list", catId: "all", title: t("shelf.everything"), catKind: "all" })}>
          <span className="cat-ic" style={{ color: "var(--ink-3)" }} aria-hidden="true"><CatIcon kind="all" /></span>
          <span className="title">{t("shelf.everything")}</span>
          <span className="meta">{total}</span>
        </button>
      </nav>

      {total === 0 && (
        <div className="empty">
          <p>{t("shelf.empty")}</p>
          <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>{Ic.plus({ size: 14 })} {t("shelf.add")}</button>
        </div>
      )}
    </div>
  );
}

function ItemList(props: {
  q: string;
  setQ: (q: string) => void;
  view: ListView;
  subcats: Category[];
  cats: Category[];
  onBack: () => void;
  openCat: (c: Category) => void;
  restored: BulkDraft | null;
  consumeRestored: () => void;
}) {
  const t = useT();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [selecting, setSelecting] = useState(!!props.restored);
  const [picked, setPicked] = useState<string[]>(props.restored?.picked ?? []);
  const [editing, setEditing] = useState(props.restored?.editing ?? false);
  const [edit, setEdit] = useState<BulkEdit>(props.restored?.edit ?? {});
  const [applying, setApplying] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // the draft is used once; a later list in the same visit starts clean
  const { consumeRestored } = props;
  useEffect(() => { consumeRestored(); }, [consumeRestored]);
  const [adding, setAdding] = useState(false);
  const [toast, say] = useToast();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.settings.get });
  const paged = settings?.appearance.browsing === "paged";
  const searching = props.q.trim().length > 0;
  const catFilter = !searching && props.view.catId !== "all" && props.view.catId !== "unsorted" ? props.view.catId : null;

  const query = useInfiniteQuery({
    queryKey: ["items", "shelf", props.view.catId, searching ? props.q : ""],
    queryFn: ({ pageParam }) =>
      api.items.list({
        limit: "50",
        ...(pageParam ? { cursor: pageParam } : {}),
        ...(catFilter ? { categoryId: catFilter } : {}),
        ...(searching ? { q: props.q.trim() } : {}),
      }),
    initialPageParam: "",
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const items = useMemo(() => {
    const flat = (query.data?.pages ?? []).flatMap((p) => p.items);
    if (props.view.catId === "unsorted" && !searching) return flat.filter((i) => !i.categoryId);
    return flat;
  }, [query.data, props.view.catId, searching]);

  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (paged || !sentinel.current) return;
    const ob = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
    });
    ob.observe(sentinel.current);
    return () => ob.disconnect();
  }, [paged, query.hasNextPage, query.isFetchingNextPage, query]);

  // while the bulk bar is up, the root carries data-bulk so the floating feedback button moves clear of it (feedback F9a)
  useEffect(() => {
    if (!selecting) return;
    document.documentElement.dataset["bulk"] = "";
    return () => { delete document.documentElement.dataset["bulk"]; };
  }, [selecting]);

  const toggle = (id: string) => setPicked(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);

  // Nothing ticked is lost (round 3 autosave law, D-37): while selecting, the
  // selection and the sheet's choices live on the device until applied or cancelled.
  const { catId, title, catKind } = props.view;
  useEffect(() => {
    if (!selecting) return;
    drafts.write(BULK_EDIT_KEY, { view: { kind: "list", catId, title, catKind }, q: props.q, picked, editing, edit } satisfies BulkDraft);
  }, [selecting, picked, editing, edit, catId, title, catKind, props.q]);

  const leaveSelect = () => {
    drafts.clear(BULK_EDIT_KEY);
    setPicked([]);
    setEdit({});
    setEditing(false);
    setEditError(null);
    setSelecting(false);
    setConfirmDelete(false);
  };
  const bulk = async (actions: Parameters<typeof api.items.bulk>[0]["action"][], done: "applied" | "saved") => {
    const n = picked.length;
    setApplying(true);
    setEditError(null);
    try {
      for (const action of actions) await api.items.bulk({ itemIds: picked, action });
    } catch (e) {
      setEditError(e instanceof Error && e.message ? e.message : t("shelf.applyFailed"));
      return;
    } finally {
      setApplying(false);
    }
    leaveSelect();
    await qc.invalidateQueries({ queryKey: ["items"] });
    await qc.invalidateQueries({ queryKey: ["categories"] });
    say(done === "applied" ? t("shelf.applied", { n }) : t("saved"));
  };
  /** Edit a vocabulary where it lives; the selection and the sheet wait in the draft (D-37). */
  const detour = (to: Detour) => void nav({ to, search: { back: "/shelf" } as never });

  return (
    <div style={{ paddingBottom: selecting ? 72 : 0 }}>
      <div className="screen-head">
        {selecting ? (
          <button className="icon-btn" onClick={leaveSelect}>{t("cancel")}</button>
        ) : (
          <button className="icon-btn" onClick={props.onBack} aria-label={t("back")}>{Ic.back()}</button>
        )}
        <h1 className="display" style={{ flex: 1, textAlign: selecting ? "center" : "left", fontSize: selecting ? "var(--fs-title)" : undefined }}>
          {selecting ? t("shelf.selected", { n: picked.length }) : searching ? t("shelf.title") : props.view.title}
        </h1>
        {selecting ? (
          <button className="icon-btn accent" onClick={() => setPicked(items.map((i) => i.id))}>{t("all")}</button>
        ) : (
          <>
            {items.length > 0 && <button className="icon-btn accent" onClick={() => setSelecting(true)}>{t("select")}</button>}
            <button type="button" className="btn btn-primary" aria-label={t("shelf.add")} style={{ minWidth: 44, padding: 0 }} onClick={() => setAdding(true)}>{Ic.plus({ size: 16 })}</button>
          </>
        )}
      </div>

      {!selecting && (
        <div className="field">
          <label htmlFor="shelf-search" className="visually-hidden">{t("search")}</label>
          <input id="shelf-search" className="input" type="search" placeholder={t("search")} value={props.q} onChange={(e) => props.setQ(e.target.value)} />
        </div>
      )}

      {!searching && !selecting && props.subcats.length > 0 && (
        <div className="chip-row" style={{ marginBottom: "var(--sp-3)" }}>
          {props.subcats.map((c) => (
            <button key={c.id} className="chip" onClick={() => props.openCat(c)}>{c.name} · {c.itemCount ?? 0}</button>
          ))}
        </div>
      )}

      {items.length === 0 ? (
        <div className="empty">
          {searching ? <p>{t("shelf.noMatch")}</p> : (
            <>
              <p>{t("shelf.empty")}</p>
              <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>{Ic.plus({ size: 14 })} {t("shelf.add")}</button>
            </>
          )}
        </div>
      ) : (
        <div className="row-list">
          {items.map((item) =>
            selecting ? (
              <button key={item.id} type="button" className="pick-row" aria-pressed={picked.includes(item.id)} onClick={() => toggle(item.id)}>
                <span className={`tick ${picked.includes(item.id) ? "on" : ""}`}>{picked.includes(item.id) && Ic.check({ size: 14 })}</span>
                <Cover item={item} size="s" showThumbs={settings?.appearance.thumbnails ?? true} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.title}</span>
                  <span className="meta">{metaLine(item, props.cats, t)}</span>
                </span>
              </button>
            ) : (
              <Row key={item.id} item={item} cats={props.cats} showThumb={settings?.appearance.thumbnails ?? true} />
            )
          )}
        </div>
      )}

      {paged
        ? query.hasNextPage && (
            <button className="btn btn-block" style={{ marginTop: "var(--sp-4)" }} onClick={() => query.fetchNextPage()}>{t("shelf.showMore")}</button>
          )
        : <div ref={sentinel} aria-hidden="true" />}

      {selecting && (
        <div className="bulk-bar" role="toolbar" aria-label={t("select")}>
          <span style={{ display: "inline-flex", gap: "var(--sp-2)" }}>
            <button className="chip" disabled={picked.length === 0} onClick={() => setEditing(true)}>{Ic.edit({ size: 12 })} {t("shelf.edit")}</button>
          </span>
          <button className="icon-btn danger" disabled={picked.length === 0} onClick={() => setConfirmDelete(true)}>{Ic.trash({ size: 14 })} {t("delete")}</button>
        </div>
      )}

      {editing && (
        <BulkEditSheet n={picked.length} edit={edit} cats={props.cats} pending={applying} error={editError}
          onChange={setEdit}
          onApply={() => void bulk(bulkActions(edit), "applied")}
          onClose={() => { setEditing(false); setEditError(null); }}
          onDetour={detour} />
      )}

      {adding && <QuickAdd onClose={() => setAdding(false)} onImported={(n) => say(t("quick.imported", { n }))} />}
      {confirmDelete && (
        <Dialog title={t("shelf.deleteQ", { n: picked.length })} onClose={() => setConfirmDelete(false)}>
          <div className="dialog-actions">
            <button className="btn" onClick={() => setConfirmDelete(false)}>{t("keep")}</button>
            <button className="btn btn-danger" onClick={() => void bulk([{ kind: "delete" }], "saved")}>{t("delete")}</button>
          </div>
        </Dialog>
      )}
      <Toast msg={toast} />
    </div>
  );
}

function Row({ item, cats, showThumb }: { item: Item; cats: Category[]; showThumb: boolean }) {
  const t = useT();
  return (
    <Link className="row" to="/item/$id" params={{ id: item.id }}>
      <Cover item={item} size="s" showThumbs={showThumb} />
      <div className="row-body">
        <div className="row-title">{item.pinned ? <span style={{ display: "inline-flex", verticalAlign: "-2px", color: "var(--accent-ink)", marginRight: 4 }}>{Ic.pinFilled({ size: 12 })}</span> : null}{item.title}</div>
        <div className="meta">{metaLine(item, cats, t)}</div>
      </div>
    </Link>
  );
}
